import {
  type PictureTypeData,
  type PictureTypeKey,
  type AcquireBookPictures,
  type AcquireBookZenbujiko,
  type AllMattersAnalyzeOptions,
} from "@/types/acquirebook";
import { applyZenbujikoGenzaiTransform } from "@/utils/utilsAcquireBook";

// 物件コード起点の新規取得実行 payload（OpenAPI 正本 PropertyCodePictureRequestNewAcquireRequest）を組み立てる純粋関数。
//
// 所在/地番/家屋番号/不動産種別/経過日数はサーバが物件コードから解決するためフロントは送らない

/** 新規取得実行リクエスト（PropertyCodePictureRequestNewAcquireRequest 相当・キャメルケース） */
export interface PropertyCodeNewAcquireRequest extends CommonAcquireFields {
  /** 取得する登記種別リスト（空不可） */
  pictures: AcquireBookPictures[];
  /** 全部事項オプションリスト（空可） */
  zenbujikoOptions: AcquireBookZenbujiko[];
}

interface BuildPropertyCodeNewAcquireRequestParams {
  requestOptions: PictureTypeData;
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  allMattersAnalyzeOptions?: AllMattersAnalyzeOptions;
}

/** 全部事項解析オプションの既定値（フロント未指定時もサーバ既定と一致する値を明示送出する）。 */
export const DEFAULT_ALL_MATTERS_ANALYZE_OPTIONS: AllMattersAnalyzeOptions = {
  includeTitleSection: true,
  includeKoku: true,
  includeOtsuku: true,
  analyzeMethodType: "rule_based",
};

/**
 * 新規取得・リユース実行 payload が共通で持つフィールド（pictures/reusePictures 以外）。
 * 解析オプション4項目（includeTitleSection/includeKoku/includeOtsuku/analyzeMethodType）は
 * AllMattersAnalyzeOptions と同一定義のため extends で取り込み、型の二重管理を避ける。
 */
export interface CommonAcquireFields extends AllMattersAnalyzeOptions {
  /** 所有者事項解析を行うか */
  withOwnerAnalyze: boolean;
  /** 全部事項解析を行うか */
  withAllMattersAnalyze: boolean;
  /** 識別名（空文字可・最大256） */
  identificationName: string;
}

interface BuildCommonAcquireFieldsParams {
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  allMattersAnalyzeOptions?: AllMattersAnalyzeOptions;
}

/**
 * 新規取得・リユース実行 payload の共通フィールドを組み立てる。
 * 解析オプションは未指定時に DEFAULT_ALL_MATTERS_ANALYZE_OPTIONS で補完する。
 * new/reuse の両ビルダーで共用し、フィールドの重複定義を解消する。
 */
export const buildCommonAcquireFields = (
  params: BuildCommonAcquireFieldsParams
): CommonAcquireFields => {
  // withAllMattersAnalyze=false のときは、ZENBUJIKO 解除後に内部状態へ残った
  // allMattersAnalyzeOptions が送信されないよう既定値へ固定する（送信時防御・BE 側も無視する）。
  const analyzeOptions = params.withAllMattersAnalyze
    ? params.allMattersAnalyzeOptions ?? DEFAULT_ALL_MATTERS_ANALYZE_OPTIONS
    : DEFAULT_ALL_MATTERS_ANALYZE_OPTIONS;
  return {
    withOwnerAnalyze: params.withOwnerAnalyze,
    withAllMattersAnalyze: params.withAllMattersAnalyze,
    identificationName: params.identificationName,
    includeTitleSection: analyzeOptions.includeTitleSection,
    includeKoku: analyzeOptions.includeKoku,
    includeOtsuku: analyzeOptions.includeOtsuku,
    analyzeMethodType: analyzeOptions.analyzeMethodType,
  };
};

/**
 * 取得対象チェックボックス（PictureTypeData）から pictures[] と zenbujikoOptions[] を抽出する。
 * 「現在事項」ON 時の _GENZAI バリアント変換は既存共通ロジック applyZenbujikoGenzaiTransform を流用する。
 * 閉鎖登記簿（HEISATOUKIBO）は本フェーズ対象外。
 * リユース実行 payload 生成でも共用する。
 */
export const extractPicturesAndZenbujikoOptions = (
  requestOptions: PictureTypeData
): {
  pictures: AcquireBookPictures[];
  zenbujikoOptions: AcquireBookZenbujiko[];
} => {
  const pictures: AcquireBookPictures[] = [];
  let zenbujikoOptions: AcquireBookZenbujiko[] = [];

  (Object.keys(requestOptions) as PictureTypeKey[]).forEach((key) => {
    switch (key) {
      case "SHOYUSHAJIKO":
        if (requestOptions.SHOYUSHAJIKO.checked) pictures.push(key);
        break;
      case "ZENBUJIKO":
        // 全部事項が未チェックの場合は、UI 非活性のまま内部状態に残った
        // サブオプションを送信しないよう checked 内にネストする。
        if (requestOptions.ZENBUJIKO.checked) {
          pictures.push(key);
          if (requestOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked) {
            zenbujikoOptions.push("SHINTAKUMOKUROKU");
          }
          if (requestOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked) {
            zenbujikoOptions.push("KYODOTANPOMOKUROKU");
          }
        }
        break;
      case "CHISEKISOKURYOZU":
        if (requestOptions.CHISEKISOKURYOZU.checked) pictures.push(key);
        break;
      case "DENSHIKOZU":
        if (requestOptions.DENSHIKOZU.checked) pictures.push(key);
        break;
      case "CHIEKIKENZUMEN":
        if (requestOptions.CHIEKIKENZUMEN.checked) pictures.push(key);
        break;
      case "TATEMONOZUMEN":
        if (requestOptions.TATEMONOZUMEN.checked) pictures.push(key);
        break;
    }
  });

  if (requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked) {
    zenbujikoOptions = applyZenbujikoGenzaiTransform(zenbujikoOptions);
  }

  return { pictures, zenbujikoOptions };
};

/**
 * 新規取得実行 payload（PropertyCodePictureRequestNewAcquireRequest）を組み立てる。
 * 地番系・経過日数は物件コードでリクエストするため送らず、引数として受け取らない。
 */
export const buildPropertyCodeNewAcquireRequest = (
  params: BuildPropertyCodeNewAcquireRequestParams
): PropertyCodeNewAcquireRequest => {
  const { pictures, zenbujikoOptions } = extractPicturesAndZenbujikoOptions(
    params.requestOptions
  );

  return {
    pictures,
    zenbujikoOptions,
    ...buildCommonAcquireFields(params),
  };
};
